import { FrontendLogger } from '@plcy-io/app-core';
import { useState } from 'react';

import { trackWaitlistSignup } from '../analytics.ts';

/** Submission lifecycle of the waitlist email form. */
export type WaitlistStatus = 'idle' | 'loading' | 'success' | 'error';

/** State + actions returned by {@link useWaitlistSignup}. */
export interface WaitlistSignup {
  readonly email: string;
  readonly setEmail: (value: string) => void;
  readonly status: WaitlistStatus;
  readonly errorMessage: string;
  /** Validates the email and submits it to the waitlist API. */
  readonly submit: () => Promise<void>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const logger = new FrontendLogger('useWaitlistSignup');

/**
 * Waitlist signup logic shared by the coming-soon page and every
 * marketing-page waitlist band.
 *
 * @remarks
 * Single source of truth for the `VITE_WAITLIST_API_URL` fetch, the
 * graceful degradation path when the API URL is not configured, and the
 * {@link trackWaitlistSignup} analytics conversion event. Extracted from
 * the original `EmailCapture` in ComingSoonPage.tsx — do not fork it.
 *
 * @param source - Attribution recorded with the signup (e.g. `'coming-soon'`,
 * `'marketing:/product/ai'`).
 */
export function useWaitlistSignup(source: string): WaitlistSignup {
  const [email, setEmail] = useState<string>('');
  const [status, setStatus] = useState<WaitlistStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  async function submit(): Promise<void> {
    if (!EMAIL_REGEX.test(email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    const apiUrl = import.meta.env['VITE_WAITLIST_API_URL'];

    if (!apiUrl) {
      logger.warn(
        'VITE_WAITLIST_API_URL is not set — using graceful degradation.'
      );
      await new Promise<void>((resolve) => setTimeout(resolve, 800));
      trackWaitlistSignup(source);
      setStatus('success');
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });

      if (response.ok) {
        trackWaitlistSignup(source);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      logger.error('Waitlist signup request failed', { error });
      setStatus('error');
      setErrorMessage('Unable to connect. Please try again later.');
    }
  }

  return { email, setEmail, status, errorMessage, submit };
}
